import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { initiateAXIOS } from "../../../../config/axios";
import { colorStyling } from "../../../../helpers/color";
import { parseVariables } from "../../../../helpers/localize";
import parser from "html-react-parser";
import {
  AdsScheduleIcon,
  AnnouncementPurpleIcon,
  ClockIcon,
  CommandScheduleIcon,
  ContentScheduleIcon,
  DotIcon,
} from "../../../../icons";
import {
  TypographyNormal,
  TypographySmall,
} from "../../../../components/customComponent";
import {
  dateFormat,
  dateOnlyFormatDDMMYYYY,
} from "../../../../helpers/dateFormat";
import { calculateDuration } from "../../../../helpers/scheduleHelper";
import { useDebounce } from "../../../../components/hooks";
import { logger } from "../../../../helpers/logger";
import { scheduleAPILimit } from "../../../../api";

const InputSearch = styled("input")(() => ({
  outline: "none",
  border: "none",
  fontSize: "16px",
  lineHeight: "26px",
  fontWeight: 400,
  width: "100%",
  marginLeft: "10px",
  fontFamily: "Inter, sans-serif",
}));

const BoxInput = styled(Box)(({ theme }) => ({
  border: `1px solid ${colorStyling.white.border}`,
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing(1.875),
  paddingRight: theme.spacing(1.875),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderRadius: "6px",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SelectEquipAnnouncemet = ({
  items = [],
  handleSelectedItem,
  disabled,
}) => {
  const { t: tCommon } = useTranslation();

  const [open, setOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState([]);
  const MAX_SELECTED = 5;

  const [loading, setLoading] = useState(false);

  const [listGetData, setListGetData] = useState([]);

  const [search, setSearch] = useState("");

  /**
   * 1 Second delay after input value change to do API fetch
   */
  const debouncedSearchTerm = useDebounce(search, 1000);

  /**
   * Fetching schedule data
   * @param {string} name Search input value
   */
  const getData = async (name) => {
    const access_token = sessionStorage.getItem("access_token");
    setLoading(true);
    try {
      let query = name ? "&nameLike=" + name : "";

      const scheduleType =
        "&type=CONTENT_SCHEDULE&type=ADVERTISEMENT&type=ANNOUNCEMENT";

      const { data } = await initiateAXIOS.get(
        scheduleAPILimit + query + scheduleType,
        {
          headers: { authorization: `Bearer ${access_token}` },
        }
      );

      setListGetData(data.items);
    } catch (e) {
      const errorMessage = e.response.data.message;
      logger.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Inital API call when dialog open
   */
  useEffect(() => {
    if (open) {
      getData();
      setSelectedItem(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items]);

  /**
   * Run api call after set value (1 second) when search box value change
   */
  useEffect(
    () => {
      if (debouncedSearchTerm) {
        getData(debouncedSearchTerm);
      } else if (debouncedSearchTerm === "" && open) {
        getData();
      } else {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  const handleClickClose = () => {
    setOpen(false);
    setListGetData([]);
    setSearch("");
    setSelectedItem([]);
  };

  const handleConfirm = () => {
    handleSelectedItem(selectedItem);
    handleClickClose();
  };

  /**
   * Handling schedule checkbox
   * @param {*} event
   */
  const handleChangeCheckbox = (event, item) => {
    if (event.target.checked) {
      if (selectedItem.length >= MAX_SELECTED) return;
      setSelectedItem([...selectedItem, item]);
    } else {
      const filtered = selectedItem.filter(
        (item) => item.id !== +event.target.value
      );
      setSelectedItem(filtered);
    }
  };

  return (
    <>
      <Box>
        <Button
          sx={{ boxShadow: 3, textTransform: "capitalize" }}
          disabled={loading || disabled}
          variant="contained"
          startIcon={<AddIcon />}
          {...(!loading && { onClick: () => setOpen(true) })}
        >
          {"Select Announcement"}
        </Button>
      </Box>

      <Dialog maxWidth="sm" open={open} onClose={handleClickClose} fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            textTransform: "capitalize",
          }}
        >
          {"Select Announcement"}
          <IconButton onClick={handleClickClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {selectedItem.length >= MAX_SELECTED ? (
            <Alert severity="warning">
              You have reached the maximum of {MAX_SELECTED} selections.
            </Alert>
          ) : (
            <Alert severity="info">
              You can select up to {MAX_SELECTED} items. Currently selected:{" "}
              {selectedItem.length}.
            </Alert>
          )}
          <BoxInput>
            <SearchIcon
              fontSize="medium"
              sx={{ color: colorStyling.gray.normal }}
            />

            <InputSearch
              type="search"
              placeholder={"Search Announcement"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </BoxInput>

          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              p: 2,
              borderRadius: "6px",
              border: `1px solid ${colorStyling.white.border}`,
              mt: 1,
            }}
          >
            {!loading && !listGetData.length ? (
              <Alert severity="info" variant="outlined">
                {parser(
                  parseVariables(tCommon("assign.noDataFound"), {
                    type: "Schedule",
                  })
                )}
              </Alert>
            ) : null}

            {loading && (
              <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                sx={{ py: 1 }}
              >
                <CircularProgress
                  size={20}
                  thickness={3}
                  sx={{ color: colorStyling.primary }}
                />
                <TypographyNormal
                  sx={{
                    color: colorStyling.primary,
                    marginTop: "15px",
                    fontWeight: 300,
                  }}
                >
                  {tCommon("loadingDataList")}
                </TypographyNormal>
              </Box>
            )}

            {listGetData?.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <ScheduleItem schedule={item} />

                <Checkbox
                  disabled={
                    loading ||
                    (selectedItem.length >= MAX_SELECTED &&
                      !selectedItem.some((selected) => selected.id === item.id))
                  }
                  checked={selectedItem.some(
                    (selected) => selected.id === item.id
                  )}
                  value={item.id}
                  onChange={(e) => handleChangeCheckbox(e, item)}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" fullWidth onClick={handleClickClose}>
            {tCommon("cancelBtn")}
          </Button>

          <Button variant="contained" fullWidth onClick={handleConfirm}>
            {tCommon("confirmBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectEquipAnnouncemet;

const ScheduleItem = ({ schedule }) => {
  const { t } = useTranslation("schedule");

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {schedule.type === "ANNOUNCEMENT" ? (
        <AnnouncementPurpleIcon />
      ) : schedule.type === "ADVERTISEMENT" ? (
        <AdsScheduleIcon />
      ) : schedule.type.includes("CONTENT") ? (
        <ContentScheduleIcon />
      ) : (
        <CommandScheduleIcon />
      )}

      <Box
        sx={{
          ml: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TypographyNormal sx={{ fontWeight: 600, mb: 1 }}>
          {schedule.name}
        </TypographyNormal>
        {schedule.type === "ADVERTISEMENT" && (
          <Box display="inline-flex" flexWrap="wrap" alignItems="center">
            <TypographySmall
              display="flex"
              alignItems="center"
              sx={{
                mx: 0.5,
                fontWeight: 400,
              }}
            >
              {dateOnlyFormatDDMMYYYY(schedule.startAt) +
                " - " +
                (schedule.endAt
                  ? dateOnlyFormatDDMMYYYY(schedule.endAt)
                  : t("item.endTimeIndefinitely"))}
            </TypographySmall>

            <svg
              width="6"
              height="6"
              viewBox="0 0 6 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="3" cy="3" r="3" fill={colorStyling.gray.normal} />
            </svg>

            <TypographySmall
              display="flex"
              alignItems="center"
              sx={{
                mx: 0.5,
                fontWeight: 400,
              }}
            >
              {"Slot " + calculateDuration(+schedule.advertDuration)}
            </TypographySmall>
          </Box>
        )}
        {schedule.type !== "ANNOUNCEMENT" &&
          schedule.type !== "ADVERTISEMENT" && (
            <Box display="flex" alignItems="center">
              <ClockIcon />
              <TypographySmall
                sx={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  textTransform: "uppercase",
                  color: colorStyling.primary,
                  fontWeight: 400,
                }}
              >
                {schedule.frequency === "OFF" ? (
                  <>
                    {schedule.type.includes("CONTENT") ? (
                      <>
                        <Tooltip
                          arrow
                          disableInteractive
                          placement="top"
                          title={t("item.explainLOOP")}
                        >
                          {t("frequency.LOOP")}
                        </Tooltip>
                      </>
                    ) : (
                      t("frequency.ONCE")
                    )}
                  </>
                ) : (
                  t(`frequency.${schedule.frequency}`, {
                    defaultValue: null,
                  }) ?? schedule.frequency
                )}
              </TypographySmall>
              <DotIcon />
              <TypographySmall sx={{ marginLeft: "5px", fontWeight: 300 }}>
                {dateFormat(schedule.startAt)}
                {schedule.endAt ? " - " + dateFormat(schedule.endAt) : ""}
              </TypographySmall>
            </Box>
          )}
      </Box>
    </Box>
  );
};
